import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Theme Service
 * Manages light/dark mode toggling with cookie persistence
 * Uses cookies (not localStorage) to share theme preference across subdomains
 * Watches for cookie changes to sync theme across browser tabs/windows
 * Applies theme by setting [data-theme] attribute on document.body
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private readonly THEME_COOKIE_NAME = 'adn-theme';
  private readonly COOKIE_DOMAIN = this.getRootDomain();
  private readonly COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds
  private readonly POLL_INTERVAL = 1000; // Check every second
  private currentTheme = new BehaviorSubject<'light' | 'dark'>('light');
  private pollIntervalId: number | null = null;
  private lastKnownTheme: string | null = null;

  /**
   * Observable stream of current theme state
   */
  public theme$: Observable<'light' | 'dark'> = this.currentTheme.asObservable();

  constructor() {
    this.initializeTheme();
    this.startWatchingCookieChanges();
  }

  ngOnDestroy(): void {
    this.stopWatchingCookieChanges();
  }

  /**
   * Get the root domain for cookie sharing across subdomains
   * For americandronenetwork.com, returns .americandronenetwork.com
   * For localhost, returns empty string (no domain restriction)
   */
  private getRootDomain(): string {
    const hostname = window.location.hostname;

    // Localhost - don't set domain
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }

    // Check if it's an IP address
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return '';
    }

    // Extract root domain (last two parts)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      // Return .domain.tld format for subdomain sharing
      return '.' + parts.slice(-2).join('.');
    }

    return '';
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  }

  /**
   * Set cookie with domain for cross-subdomain sharing
   */
  private setCookie(name: string, value: string): void {
    let cookieString = `${name}=${value}; path=/; max-age=${this.COOKIE_MAX_AGE}; SameSite=Lax`;

    // Add domain for subdomain sharing (only if we have a root domain)
    if (this.COOKIE_DOMAIN) {
      cookieString += `; domain=${this.COOKIE_DOMAIN}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Remove cookie
   */
  private removeCookie(name: string): void {
    let cookieString = `${name}=; path=/; max-age=0`;

    if (this.COOKIE_DOMAIN) {
      cookieString += `; domain=${this.COOKIE_DOMAIN}`;
    }

    document.cookie = cookieString;
  }

  /**
   * Start watching for cookie changes from other tabs/windows
   */
  private startWatchingCookieChanges(): void {
    this.lastKnownTheme = this.getCookie(this.THEME_COOKIE_NAME);

    // Poll for cookie changes
    this.pollIntervalId = window.setInterval(() => {
      this.checkForCookieChanges();
    }, this.POLL_INTERVAL);

    // Also check when tab becomes visible (user switches back to this tab)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Stop watching for cookie changes
   */
  private stopWatchingCookieChanges(): void {
    if (this.pollIntervalId !== null) {
      window.clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle visibility change - check cookie when tab becomes visible
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkForCookieChanges();
    }
  };

  /**
   * Check if cookie value has changed and update theme if needed
   */
  private checkForCookieChanges(): void {
    const currentCookieValue = this.getCookie(this.THEME_COOKIE_NAME);

    if (currentCookieValue !== this.lastKnownTheme) {
      this.lastKnownTheme = currentCookieValue;

      if (currentCookieValue === 'light' || currentCookieValue === 'dark') {
        // Only update if different from current state (avoid redundant updates)
        if (currentCookieValue !== this.currentTheme.value) {
          this.applyTheme(currentCookieValue);
        }
      }
    }
  }

  /**
   * Apply theme without setting cookie (for syncing from external changes)
   */
  private applyTheme(theme: 'light' | 'dark'): void {
    this.currentTheme.next(theme);
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }

  /**
   * Initialize theme on app startup
   * Priority: cookie > system preference > light (default)
   */
  private initializeTheme(): void {
    const savedTheme = this.getCookie(this.THEME_COOKIE_NAME) as 'light' | 'dark' | null;

    if (savedTheme === 'light' || savedTheme === 'dark') {
      // Use saved preference
      this.setTheme(savedTheme);
    } else {
      // Detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(prefersDark ? 'dark' : 'light');
    }

    // Listen for system preference changes (optional enhancement)
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!this.getCookie(this.THEME_COOKIE_NAME)) {
        this.setTheme(e.matches ? 'dark' : 'light');
      }
    });
  }

  /**
   * Set theme to specific mode
   * @param theme - 'light' or 'dark'
   */
  public setTheme(theme: 'light' | 'dark'): void {
    this.applyTheme(theme);
    this.setCookie(this.THEME_COOKIE_NAME, theme);
    this.lastKnownTheme = theme; // Update last known to prevent self-triggering
  }

  /**
   * Toggle between light and dark modes
   */
  public toggleTheme(): void {
    const newTheme = this.currentTheme.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Get current theme (synchronous)
   * @returns Current theme value
   */
  public getCurrentTheme(): 'light' | 'dark' {
    return this.currentTheme.value;
  }

  public setSavedTheme() {
    this.setTheme(this.getCurrentTheme())
  }

  public setTransientTheme(theme: 'light' | 'dark' = 'dark') {
    document.documentElement.setAttribute('data-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }

  /**
   * Check if current theme is dark mode
   * @returns true if dark mode is active
   */
  public isDarkMode(): boolean {
    return this.currentTheme.value === 'dark';
  }

  /**
   * Clear saved theme preference
   * Reverts to system preference detection
   */
  public clearThemePreference(): void {
    this.removeCookie(this.THEME_COOKIE_NAME);
    this.lastKnownTheme = null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.setTheme(prefersDark ? 'dark' : 'light');
  }
}
